import { FC, useEffect, useRef } from "react";
import { Editable, EditablePreview, EditableTextarea, HStack, Textarea, Box } from "@chakra-ui/react"
import { packsClient } from "lib/clients";
import { useLoggedinUser, usePack, useRunSync, useToastNotification } from "lib/hooks";
import autosize from 'autosize';
import { RiShieldKeyholeLine } from '@react-icons/all-files/ri/RiShieldKeyholeLine';
import { isAdmin, isOwner, isPreviewingPack } from "lib/utils";
import { useRouter } from "next/router";
import { usePackContext } from "./context";

export const PackTitle:FC = () => {
  const { packId } = usePackContext();
  const { data: pack, mutate } = usePack(packId);
  const { sync } = useRunSync(packId);
  const { data: user } = useLoggedinUser();
  const router = useRouter();
  const { errorToast } = useToastNotification();

  const ref = useRef(null);

  useEffect(() => {
    if (!ref.current) return;

    autosize(ref.current);

    return () => {
      autosize.destroy(ref.current);
    }
  }, [ref]);

  const isEditable = !isPreviewingPack(router?.pathname) && (isAdmin(pack) || isOwner(pack, user));

  const onChange = async (title: string) => {
    await mutate({ ...pack, title }, false);
  }

  const onSubmit = async (title: string): Promise<void> => {
    try {
      await mutate({ ...pack, title }, false);
      await packsClient.updatePack(packId, { title });
      await sync();
    } catch (e) {
      errorToast({ title: 'Success', description: "The asset has been updated." });
    }
  }

  return (
    <>
      <Editable
        value={pack?.title}
        data-help="Enter the name of your pack here."
        selectAllOnFocus={false}
        onChange={onChange}
        onSubmit={onSubmit}
        isDisabled={!isEditable}
        fontSize={["xx-large", "xxx-large"]}
        fontWeight={"600"}
        textAlign="center"
        mt={["20px", "0"]}
        mb={["20px", "40px"]}
        data-testid="pack-title"
        variant={!isEditable ? 'non-editable' : ''}
        display="inline-block"
        width="100%"
      >
        <HStack justifyContent="center" width="100%">
          <EditablePreview data-testid="pack-title-preview" whiteSpace="pre-line" />
          <Textarea py="6px" onFocus={() => autosize.update(ref.current)} ref={ref} width="100%" data-testid="pack-title-input" as={EditableTextarea} fontSize={["xx-large", "xxx-large"]} fontWeight={"600"} border="1px dashed transparent" borderColor="brand.500" _hover={{borderColor: 'brand.500'}} _focus={{boxShadow: 'none'}} />
          {pack?.is_encrypted && (
            <Box flex="0 0 48px">
              <RiShieldKeyholeLine display="inline-block" size={48} />
            </Box>
          )}
        </HStack>
      </Editable>
    </>  
  )
}
